/**
 * App-level actions for session uploads.
 *
 * Deliberately thin: it owns the "when do we load" decision and the
 * "keep the just-uploaded file visible without a refresh" merge, and leaves
 * fetching to `controllers/session-uploads.ts`.
 */

import type { OpenClawApp as AppState } from "./app.ts";
import {
  emptySessionUploadsState,
  loadSessionUploads,
  mergeUploadedFile,
  resetForSession,
  type SessionUploadsState,
} from "./controllers/session-uploads.ts";
import { findTranscriptMessageIdForFile } from "./controllers/session-uploads.ts";
import type { SessionFileRecord } from "./chat/session-files.ts";

/** Load every file stored for `sessionKey`. */
export async function refreshSessionUploads(app: AppState, sessionKey?: string): Promise<void> {
  const key = (sessionKey ?? app.sessionKey ?? "").trim();
  if (!key) {
    app.sessionUploads = { ...emptySessionUploadsState() };
    return;
  }
  app.sessionUploads = { ...resetForSession(app.sessionUploads, key), sessionKey: key, loading: true };
  const files = await loadSessionUploads({
    origin: app.client?.getHttpOrigin?.() ?? "",
    token: app.client?.token?.(),
    sessionKey: key,
  });
  app.sessionUploads = { sessionKey: key, files, loading: false, error: null, loaded: true };
}

/**
 * Merge a freshly-stored upload into the list.
 *
 * Called from the composer's upload callback so the Files tab is correct the
 * instant the operator's file lands — no Refresh, no waiting for a list
 * round-trip.
 */
export function noteUploadedSessionFile(
  app: AppState,
  sessionKey: string,
  file: SessionFileRecord,
): void {
  const next = mergeUploadedFile(app.sessionUploads, sessionKey, file);
  app.sessionUploads = next satisfies SessionUploadsState;
}

/** Clear the list when the operator switches to a session we have not loaded. */
export function resetSessionUploadsOnSwitch(app: AppState, sessionKey: string | null): void {
  app.sessionUploads = resetForSession(app.sessionUploads, sessionKey);
}

/**
 * Scroll the transcript to the message that referenced a file.
 *
 * The id comes from the persisted user row, so this works for uploads from
 * earlier turns and after a reload — not just the optimistic message.
 */
export function jumpToSessionFileInTranscript(app: AppState, fileId: string): void {
  const messages = app.chatMessages as unknown[] | undefined;
  const messageId = findTranscriptMessageIdForFile(messages, fileId);
  if (!messageId) return;
  const el = document.querySelector(`[data-msg-id="${CSS.escape(messageId)}"]`);
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
}